import { useState, useEffect, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { RotateCw, Sun, Contrast, Plus, Camera, Trash2, Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { rotatedPreview, renderPage, buildDocumentFile } from "@/lib/imageProcessing";

const FULL = { unit: "%", x: 4, y: 4, width: 92, height: 92 };

function PageEditor({ src, onUse, onRetake, onCancel }) {
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [crop, setCrop] = useState(FULL);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    rotatedPreview(src, rotation).then((v) => { if (live) { setView(v); setCrop(FULL); } });
    return () => { live = false; };
  }, [src, rotation]);

  const use = async () => {
    setBusy(true);
    try { onUse(await renderPage(src, { rotation, crop, brightness, contrast })); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="capture-page-editor">
      <p className="text-xs text-navy/60">Drag the corners of the frame to crop to the edges of the document.</p>
      <div className="bg-navy/5 rounded-lg flex items-center justify-center min-h-[220px]">
        {view ? (
          <ReactCrop crop={crop} onChange={(_, pc) => setCrop(pc)} keepSelection data-testid="crop-area">
            <img src={view} alt="Captured page" className="max-h-[45vh] w-auto"
              style={{ filter: `brightness(${brightness}%) contrast(${contrast}%)` }} />
          </ReactCrop>
        ) : <Loader2 className="w-6 h-6 animate-spin text-gold" />}
      </div>
      <Slider icon={Sun} label="Brightness" value={brightness} onChange={setBrightness} testid="brightness-slider" />
      <Slider icon={Contrast} label="Contrast" value={contrast} onChange={setContrast} testid="contrast-slider" />
      <div className="grid grid-cols-3 gap-2">
        <Button type="button" variant="outline" className="min-h-[44px]" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="rotate-btn">
          <RotateCw className="w-4 h-4 mr-1" /> Rotate
        </Button>
        <Button type="button" variant="outline" className="min-h-[44px]" onClick={onRetake} data-testid="retake-btn">
          <Camera className="w-4 h-4 mr-1" /> Retake
        </Button>
        <Button type="button" variant="outline" className="min-h-[44px]" onClick={onCancel} data-testid="adjust-cancel-btn">Cancel</Button>
      </div>
      <Button type="button" onClick={use} disabled={busy || !view} className="w-full min-h-[48px] bg-gold text-navy hover:bg-gold-light" data-testid="use-page-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Use this page</>}
      </Button>
    </div>
  );
}

function Slider({ icon: Icon, label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-navy/50 shrink-0" />
      <span className="text-xs text-navy/60 w-20">{label}</span>
      <input type="range" min="50" max="200" value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-8 accent-gold" aria-label={label} data-testid={testid} />
      <span className="text-xs text-navy/50 w-10 text-right">{value}%</span>
    </div>
  );
}

function PageReview({ pages, onRetake, onDelete, onAdd, onConfirm, busy }) {
  return (
    <div className="space-y-3" data-testid="capture-review">
      <div className="grid grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto">
        {pages.map((p, i) => (
          <div key={i} className="border border-navy/10 rounded-lg p-2 space-y-2" data-testid={`capture-page-${i + 1}`}>
            <img src={p.dataUrl} alt={`Page ${i + 1}`} className="w-full h-32 object-contain bg-navy/5 rounded" />
            <p className="text-xs font-medium text-navy">Page {i + 1}</p>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="outline" className="flex-1 min-h-[44px] text-xs" onClick={() => onRetake(i)} data-testid={`retake-page-${i + 1}-btn`}>Retake</Button>
              <Button type="button" size="sm" variant="outline" className="min-h-[44px] min-w-[44px] text-red-500" onClick={() => onDelete(i)} aria-label={`Delete page ${i + 1}`} data-testid={`delete-page-${i + 1}-btn`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" className="w-full min-h-[48px]" onClick={onAdd} data-testid="add-page-btn">
        <Plus className="w-4 h-4 mr-1" /> Add another page
      </Button>
      <Button type="button" onClick={onConfirm} disabled={busy || !pages.length} className="w-full min-h-[48px] bg-gold text-navy hover:bg-gold-light" data-testid="confirm-capture-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm & save (${pages.length} page${pages.length === 1 ? "" : "s"}${pages.length > 1 ? ", one PDF" : ""})`}
      </Button>
    </div>
  );
}

export default function CaptureDialog({ open, initialSrc, title, baseName, onClose, onConfirm }) {
  const [pages, setPages] = useState([]);
  const [editSrc, setEditSrc] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);

  useEffect(() => {
    if (open) { setPages([]); setEditSrc(initialSrc); setSlot(null); }
  }, [open, initialSrc]);

  const capture = (index) => { setSlot(index); camRef.current?.click(); };
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) setEditSrc(URL.createObjectURL(f));
  };
  const usePage = (page) => {
    setPages((p) => (slot === null ? [...p, page] : p.map((x, i) => (i === slot ? page : x))));
    setEditSrc(null);
    setSlot(null);
  };
  const cancelEdit = () => { setEditSrc(null); setSlot(null); if (!pages.length) onClose(); };
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(await buildDocumentFile(pages, baseName), pages[0].dataUrl, pages.length); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-lg p-4 max-h-[95vh] overflow-y-auto" data-testid="adjust-document-dialog">
        <DialogHeader>
          <DialogTitle className="text-navy">{title}</DialogTitle>
          <DialogDescription className="text-xs">Manual crop. Edges are not detected automatically.</DialogDescription>
        </DialogHeader>
        {editSrc
          ? <PageEditor src={editSrc} onUse={usePage} onRetake={() => capture(slot)} onCancel={cancelEdit} />
          : <PageReview pages={pages} busy={busy} onRetake={capture} onAdd={() => capture(null)} onConfirm={confirm}
              onDelete={(i) => setPages((p) => p.filter((_, j) => j !== i))} />}
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="capture-next-page-input" />
      </DialogContent>
    </Dialog>
  );
}
